import PropTypes from 'prop-types';
import { use, useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import FlotiqPlugins from '../../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { FormConfigFieldEvent } from '../../../lib/flotiq-plugins/plugin-events/FormConfigFieldEvent';
import { FormRenderFieldEvent } from '../../../lib/flotiq-plugins/plugin-events/FormRenderFieldEvent';
import { EMPTY_OBJECT } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';

import ElementFromPlugin from '../../../components/ElementFromPlugin/ElementFromPlugin';
import OverrideSwitch from '../../../components/OverrideSwitch/OverrideSwitch';

import useMediaUpload from '../../../hooks/api/useMediaUpload';
import usePluginResults from '../../../hooks/usePluginResults';

import ContentObjectFormContext from '../../../contexts/ContentObjectFormContext';

import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';
import { useFormAsFormikApi } from '../../useFormAsFormikApi';

const findInputType = (props) => {
  switch (props.inputType) {
    case 'dateTime':
      return props.showTime ? 'datetime-local' : 'date';
    case 'number':
      return 'number';
    case 'email':
      return 'email';
    default:
      return 'text';
  }
};

/**
 * @emits FlotiqPlugins."flotiq.form.field::render"
 * @emits FlotiqPlugins."flotiq.form.field::config"
 * @emits FlotiqPlugins."flotiq.form.field.listeners::add"
 */
const CtoCustomField = ({
  properties = EMPTY_OBJECT,
  schema = EMPTY_OBJECT,
  isRequired = false,
  disabled = false,
  readonly = false,
  label = '',
  isCtdForm = false,
  helpText,
  additionalClasses = '',
  testId = '',
  ...props
}) => {
  const {
    contentType,
    initialData,
    isEditing,
    userPlugins,
    overriddenFields,
    isPatchable,
    formUniqueKey,
  } = use(ContentObjectFormContext);
  const { onUpload: onMediaUpload } = useMediaUpload();

  const form = useFormContext();
  const formProps = useFormAsFormikApi();
  const field = useFieldContext();
  const name = field.name;

  const fieldLabel = label || properties.label || name;
  const isOverridden = !isPatchable || overriddenFields[name];
  const hasSwitch = isPatchable && !name.includes('].');
  const isDisabled =
    (isEditing && properties.readonly) ||
    disabled ||
    (!isOverridden && hasSwitch);

  const getFieldComponent = useCallback(
    (subField) => {
      switch (properties.inputType) {
        case 'object':
          return subField.ListField;
        case 'textarea':
          return subField.Textarea;
        case 'geo':
          return subField.GeoField;
        case 'block':
          return subField.BlockInput;
        case 'select':
          return subField.Dropdown;
        case 'radio':
          return subField.RadioGroup;
        case 'checkbox':
          return subField.Switch;
        case 'datasource':
          return subField.RelationField;
        case 'richtext':
          return subField.RichText;
        case 'textMarkdown':
          return subField.Markdown;
        case 'simpleList':
          return subField.OptionsField;
        default:
          return subField.Input;
      }
    },
    [properties.inputType],
  );

  const getFieldProps = useCallback(() => {
    switch (properties.inputType) {
      case 'email':
      case 'text':
      case 'dateTime': {
        return { type: findInputType(properties) };
      }
      case 'number': {
        return {
          type: findInputType(properties),
        };
      }
      case 'block': {
        return {
          key: formUniqueKey,
          toolsList: properties.blockEditorTypes,
          onMediaUpload,
          additionalClasses: 'max-w-full',
        };
      }
      case 'textMarkdown':
      case 'richtext': {
        return {
          key: formUniqueKey,
          onMediaUpload,
          additionalClasses: 'max-w-full',
        };
      }
      case 'select': {
        return {
          options: properties.useOptionsWithLabels
            ? properties.optionsWithLabels || []
            : (properties.options || []).map((option) => ({
                value: option,
                label: option,
              })),
          nullable: !isRequired,
          multiple: properties.isMultiple || properties.multiple,
        };
      }
      case 'radio': {
        return {
          options: (properties.options || []).map((option) => ({
            value: option,
            label: option,
          })),
          horizontal: true,
        };
      }
      case 'checkbox': {
        return {
          theme: 'dark',
          size: 'base',
          side: 'right',
          labelUp: true,
          additionalClasses: 'max-w-2xl !justify-items-start',
        };
      }
      case 'object': {
        return {
          itemsProps: properties.items,
          itemsSchema: schema.items,
          isEditing: isEditing,
          isFormDisabled: isDisabled,
          additionalClasses: 'max-w-full',
          isCtdForm,
        };
      }
      case 'datasource': {
        return {
          validation: properties.validation,
          onMediaUpload,
        };
      }
      case 'textarea': {
        return {
          additionalClasses: 'max-w-full',
        };
      }
      default:
        return {};
    }
  }, [
    properties,
    formUniqueKey,
    onMediaUpload,
    isRequired,
    schema.items,
    isEditing,
    isDisabled,
    isCtdForm,
  ]);

  const errors = useFieldErrors();

  const eventParams = useMemo(
    () => ({
      name,
      value: field.state.value,
      formik: formProps,
      required: isRequired,
      disabled: isDisabled,
      readonly,
      properties,
      schema,
      error: errors,
      onMediaUpload,
      contentType,
      initialData,
      isEditing,
      userPlugins,
      formUniqueKey,
    }),
    [
      name,
      field.state.value,
      formProps,
      isRequired,
      isDisabled,
      readonly,
      properties,
      schema,
      errors,
      onMediaUpload,
      contentType,
      initialData,
      isEditing,
      userPlugins,
      formUniqueKey,
    ],
  );

  /**
   * @emits FlotiqPlugins."flotiq.form.field::render"
   */
  const pluginRenders = usePluginResults(
    'flotiq.form.field::render',
    FormRenderFieldEvent,
    eventParams,
  );

  /**
   * @emits FlotiqPlugins."flotiq.form.field.listeners::add"
   */
  const fieldListeners = usePluginResults(
    'flotiq.form.field.listeners::add',
    FormRenderFieldEvent,
    eventParams,
  );

  const groupedListeners = useMemo(
    () =>
      fieldListeners.reduce((allListeners, pluginListeners) => {
        Object.entries(pluginListeners).forEach(([type, listener]) => {
          if (!allListeners[type]) allListeners[type] = [];
          allListeners[type].push(listener);
        });
        return allListeners;
      }, {}),
    [fieldListeners],
  );

  const finalListeners = useMemo(
    () => ({
      ...['onChange', 'onBlur', 'onMount', 'onSubmit']
        .filter((type) => groupedListeners[type]?.length > 0)
        .reduce((allListeners, type) => {
          allListeners[type] = (...props) => {
            groupedListeners[type].forEach((listener) => {
              listener(...props);
            });
          };
          return allListeners;
        }, {}),
    }),
    [groupedListeners],
  );

  /**
   * @emits FlotiqPlugins."flotiq.form.field::config"
   */
  const fieldProps = useMemo(() => {
    const uniqueFieldProps = getFieldProps();
    const fieldProps = { name, ...uniqueFieldProps };

    FlotiqPlugins.run(
      'flotiq.form.field::config',
      new FormConfigFieldEvent({
        ...eventParams,
        config: fieldProps,
      }),
    );
    return fieldProps;
  }, [getFieldProps, name, eventParams]);

  const {
    key,
    additionalElements,
    additionalClasses: additionalFieldClasses,
    ...fieldProperties
  } = useMemo(() => fieldProps, [fieldProps]);

  if (pluginRenders?.length > 0) {
    return <ElementFromPlugin results={pluginRenders} />;
  }

  const fieldRowClasses =
    isPatchable && hasSwitch
      ? 'grid grid-cols-[fit-content(100px)_1fr] gap-2 md:gap-10'
      : 'min-w-0 w-full';

  if (!isCtdForm && properties.hidden) return null;
  if (['object', 'datasource', 'simpleList'].indexOf(properties.inputType) > -1)
    return (
      <div className={fieldRowClasses}>
        {hasSwitch && (
          <OverrideSwitch
            name={fieldProperties.name}
            readonly={properties.readonly}
            checked={isOverridden}
            unique={properties.unique}
            testId={testId}
          />
        )}
        <div className="relative min-w-0">
          <form.AppField
            key={key}
            name={fieldProperties.name}
            mode="array"
            listeners={finalListeners}
          >
            {(subField) => {
              const FieldComponent = getFieldComponent(subField);
              return (
                <FieldComponent
                  key={key}
                  label={fieldLabel}
                  required={isRequired && (!isPatchable || isOverridden)}
                  helpText={helpText || properties.helpText}
                  disabled={isDisabled}
                  readonly={readonly}
                  additionalClasses={twMerge(
                    additionalClasses,
                    additionalFieldClasses,
                  )}
                  {...fieldProperties}
                  {...getTestProps(testId, name, 'testId')}
                />
              );
            }}
          </form.AppField>
          {additionalElements && (
            <ElementFromPlugin results={additionalElements} />
          )}
        </div>
      </div>
    );

  return (
    <div className={fieldRowClasses}>
      {hasSwitch && (
        <OverrideSwitch
          name={fieldProperties.name}
          readonly={properties.readonly}
          checked={isOverridden}
          unique={properties.unique}
          testId={testId}
        />
      )}
      <div className="relative min-w-0">
        <form.AppField
          key={key}
          name={fieldProperties.name}
          listeners={finalListeners}
        >
          {(subField) => {
            const FieldComponent = getFieldComponent(subField);
            return (
              <FieldComponent
                key={key}
                label={fieldLabel}
                required={isRequired && isOverridden}
                disabled={isDisabled}
                readonly={readonly}
                helpText={helpText || properties.helpText}
                additionalClasses={twMerge(
                  additionalClasses,
                  additionalFieldClasses,
                )}
                {...props}
                {...fieldProperties}
                {...getTestProps(testId, name, 'testId')}
              />
            );
          }}
        </form.AppField>
        {additionalElements && (
          <ElementFromPlugin results={additionalElements} />
        )}
      </div>
    </div>
  );
};

export default CtoCustomField;

CtoCustomField.propTypes = {
  /**
   * Field name
   */
  name: PropTypes.string.isRequired,
  /**
   * Cto field properties
   */
  properties: PropTypes.object,
  /**
   * Cto field schema
   */
  schema: PropTypes.object,
  /**
   * If field is required
   */
  isRequired: PropTypes.bool,
  /**
   * If form or field is disabled
   */
  disabled: PropTypes.bool,
  /**
   * If field is readonly (non interactive but without disabled classes)
   */
  readonly: PropTypes.bool,
  /**
   * Label to override from props
   */
  label: PropTypes.node,
  /**
   * If field is rendered in Content Type form
   */
  isCtdForm: PropTypes.bool,
  /**
   * Form field additional CSS classes
   */
  additionalClasses: PropTypes.string,
  /**
   * Field test id
   */
  testId: PropTypes.string,
};
